import { useState, type CSSProperties } from 'react'
import { Search, MapPin, Navigation, X } from 'lucide-react'
import { useNav } from '@/shared/hooks/use-nav'
import { TopBar } from '@/shared/components/top-bar'
import { LocationBar } from '@/shared/components/location-bar'
import { IconButton } from '@/shared/ui/icon-button'
import { SearchField } from '@/shared/ui/search-field'
import { SegmentedControl } from '@/shared/ui/segmented'
import { LiveMap } from '@/shared/ui/live-map'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { ErrorState } from '@/shared/ui/error-state'
import { ClubCardSkeleton } from '@/shared/components/club-card-skeleton'
import type { DirectoryClub } from './use-places'
import { useLugares, LUGARES_FILTERS } from './use-lugares'
import {
  usePlayerLocation,
  distanceOriginLabel,
  type DistanceOriginKind,
} from './use-player-location'

type LugaresView = 'lista' | 'mapa'

// Padel places directory — location-first: centred on a place + radius, browsable
// as a list or a map. Reveal-on-tap search + filter chips + the 4 states. Cards
// push to club detail. Swaps to a geo Firestore query later.
export function LugaresPage() {
  const navigate = useNav()
  const feed = useLugares()
  const { status, clubs, filter, setFilter, query, setQuery } = feed
  const [searching, setSearching] = useState(false)
  const [view, setView] = useState<LugaresView>('lista')

  return (
    <>
      <TopBar
        title="Lugares"
        actions={
          <IconButton
            label={searching ? 'Cerrar búsqueda' : 'Buscar'}
            onClick={() => {
              setSearching((v) => !v)
              if (searching) setQuery('')
            }}
          >
            {searching ? <X size={20} /> : <Search size={20} />}
          </IconButton>
        }
      />

      <div className="border-b border-[var(--chrome-line)]">
        <LocationBar location={feed.location} onLocationChange={feed.setLocation} />
      </div>

      {searching && (
        <div className="px-4 pt-3 um-fade">
          <SearchField autoFocus value={query} onChange={setQuery} placeholder="Buscar por club, ciudad o dirección" />
        </div>
      )}

      {/* WRAPS, matching the Partidos feed (2026-08-08). This row was still
          scrolling horizontally after the feed was fixed, so "Con clases" sat
          half off the right edge with no cue — the same discoverability defect,
          left behind because the fix wasn't propagated. Four short chips fit in
          two rows; there is no reason for a player to scroll to find a filter. */}
      <div className="flex flex-wrap shrink-0 gap-2 px-4 py-3">
        {LUGARES_FILTERS.map((f) => (
          <Chip key={f} active={f === filter} onClick={() => setFilter(f === filter ? 'Cerca de mí' : f)}>
            {f === 'Cerca de mí' && <Navigation size={13} />}
            {f}
          </Chip>
        ))}
        <DistanceOriginChip />
      </div>

      <div className="px-4 pb-3">
        <SegmentedControl<LugaresView>
          value={view}
          onChange={setView}
          options={[
            { value: 'lista', label: 'Lista' },
            { value: 'mapa', label: 'Mapa' },
          ]}
        />
      </div>

      {status === 'loading' && (
        <div className="flex flex-col gap-3 px-4 pb-6" aria-busy>
          {[0, 1, 2].map((i) => <ClubCardSkeleton key={i} />)}
        </div>
      )}

      {status === 'error' && <ErrorState onRetry={feed.retry} />}

      {status === 'empty' && (
        <EmptyState
          title={query ? `Ningún club coincide con "${query}".` : `No hay clubes en "${filter}" cerca.`}
          cta={
            query ? (
              <Button variant="outline" size="pill" onClick={() => setQuery('')}>
                Limpiar búsqueda
              </Button>
            ) : (
              <Button variant="outline" size="pill" onClick={() => setFilter('Cerca de mí')}>
                Ver todos
              </Button>
            )
          }
        />
      )}

      {status === 'loaded' && (
        <div className="flex flex-col gap-3 px-4 pb-6">
          {view === 'mapa' && (
            <div className="relative um-fade">
              {/* The discovery map — the use case that actually earns an
                  embedded map, as opposed to the club page where a deep link to
                  the player's own maps app already does the job. Only pinned
                  clubs plot; unpinned ones stay in the list rather than being
                  silently dropped. */}
              <LiveMap
                className="h-52"
                pins={clubs
                  .filter((c) => c.lat != null && c.lng != null)
                  .map((c) => ({ lat: c.lat!, lng: c.lng!, title: c.name }))}
                label={`${clubs.length} clubes cerca`}
              />
              {/* Visible count (not aria-only) for sighted users. */}
              <span className="absolute bottom-2 left-2 px-2.5 py-1 rounded-full bg-[var(--surface-card)] border border-[var(--line)] text-[length:var(--fs-xs)] font-semibold">
                {clubs.length} {clubs.length === 1 ? 'club cerca' : 'clubes cerca'}
              </span>
            </div>
          )}
          {clubs.map((club, i) => (
            <div key={club.id} className="um-enter" style={{ '--i': i } as CSSProperties}>
              <ClubRow club={club} onClick={() => navigate(`/club/${club.id}`)} />
            </div>
          ))}
        </div>
      )}
    </>
  )
}

/**
 * The contextual permission prompt — and the only place the app ever asks.
 *
 * It sits with the filters rather than firing on mount because "Cerca de mí" is
 * the DEFAULT filter: triggering off that would prompt every visitor on load,
 * before they know what the app is, which is the surest way to collect a
 * permanent denial. Browsers remember a refusal; one badly-timed dialog burns
 * the capability for that user for good.
 *
 * Once granted it becomes the undo, because a player who shared a position must
 * be able to take it back without hunting through browser settings.
 */
function DistanceOriginChip() {
  const { status, position, request, clear } = usePlayerLocation()

  if (position) {
    return (
      <Chip active onClick={clear}>
        <Navigation size={13} /> Desde mi ubicación
      </Chip>
    )
  }
  // A denial is the player's decision — stop offering, and say why the
  // distances stay measured from the centre rather than silently reverting.
  if (status === 'denied') {
    return (
      <Chip onClick={() => {}} aria-disabled>
        Ubicación bloqueada · distancias desde el centro
      </Chip>
    )
  }
  return (
    <Chip onClick={request}>
      <Navigation size={13} />
      {status === 'asking' ? 'Buscando…' : status === 'unavailable' ? 'Reintentar ubicación' : 'Usar mi ubicación'}
    </Chip>
  )
}

function ClubRow({ club, onClick }: { club: DirectoryClub; onClick: () => void }) {
  // Read straight from the store rather than drilling a prop through the list:
  // the origin is a global fact about every distance on screen, not a property
  // of one card, and threading it would let two cards disagree.
  const originKind: DistanceOriginKind = usePlayerLocation((s) =>
    s.position ? 'player' : 'centroid',
  )
  return (
    <Card onClick={onClick}>
      <div className="p-4 flex items-center gap-3">
        <span className="h-12 w-12 shrink-0 rounded-[var(--radius)] bg-[var(--accent-soft)] text-[color:var(--accent)] inline-flex items-center justify-center">
          <MapPin size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{club.name}</p>
          <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)] truncate">{club.address}</p>
          <p className="text-[length:var(--fs-xs)] text-[color:var(--fg-faint)] mt-0.5">
            {club.courts} pistas · {club.indoor ? 'Indoor' : 'Outdoor'} · {club.surface}
          </p>
        </div>
        {/* The caption names the ORIGIN, and it is load-bearing: the same
            number means "from you" or "from the city centre" depending on
            whether the player has shared a position, and a bare "3,2 km" reads
            as "from me" to everyone. Same defect class as `requesterCity` — a
            proxy presented as a fact. */}
        {club.distanceKm != null && (
          <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--accent)] shrink-0 text-right leading-tight">
            {club.distanceKm} km
            <span className="block text-[length:var(--fs-xs)] font-normal text-[color:var(--fg-faint)]">
              {distanceOriginLabel(originKind)}
            </span>
          </span>
        )}
      </div>
    </Card>
  )
}
